import { useState } from 'react';
import { ChartLineUp, UsersFour, Broadcast, ChartPieSlice, FileText, Gear, Sparkle, CaretDown } from '@phosphor-icons/react';
import AdoptionOverview from './admin/AdoptionOverview';
import TeamAccess from './admin/TeamAccess';
import ActivityFeed from './admin/ActivityFeed';
import FeatureUsage from './admin/FeatureUsage';
import UserDetailDialog from './admin/UserDetailDialog';
import { Card, CardContent } from './ui/card';

const NAV_ITEMS = [
  { key: 'adoption', label: 'AI Adoption', icon: ChartLineUp },
  { key: 'team', label: 'Team Access', icon: UsersFour },
  { key: 'activity', label: 'Activity Feed', icon: Broadcast },
  { key: 'features', label: 'Feature Usage', icon: ChartPieSlice },
  { key: 'reports', label: 'Reports', icon: FileText },
  { key: 'settings', label: 'Settings', icon: Gear },
];

function ComingSoon({ title, subtitle }) {
  return (
    <div className="flex items-center justify-center h-96">
      <Card className="bg-card border-border max-w-md text-center">
        <CardContent className="p-6">
          <div className="w-12 h-12 rounded-full bg-[#E63935]/20 text-[#E63935] flex items-center justify-center mx-auto mb-3"><Sparkle size={22} weight="fill" /></div>
          <h3 className="text-base font-bold text-foreground">{title}</h3>
          <p className="text-xs text-muted-foreground mt-2">{subtitle}</p>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminPanel() {
  const [active, setActive] = useState('adoption');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const currentUser = (() => {
    try { return JSON.parse(sessionStorage.getItem('user') || '{}'); } catch { return {}; }
  })();

  const openDetail = (row) => setDetailUser(row);

  return (
    <div className="flex flex-col lg:flex-row gap-4" data-testid="admin-panel">
      {/* Mobile top bar */}
      <div className="lg:hidden">
        <button onClick={() => setMobileOpen(!mobileOpen)}
          className="w-full flex items-center justify-between p-3 rounded-lg bg-card border border-border" data-testid="admin-mobile-nav-toggle">
          <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Sparkle size={16} weight="fill" className="text-[#E63935]" /> {NAV_ITEMS.find(n => n.key === active)?.label}
          </span>
          <CaretDown size={14} className={`text-muted-foreground transition-transform ${mobileOpen ? 'rotate-180' : ''}`} />
        </button>
        {mobileOpen && (
          <div className="mt-2 space-y-1 p-2 bg-card border border-border rounded-lg">
            {NAV_ITEMS.map(n => (
              <button key={n.key} onClick={() => { setActive(n.key); setMobileOpen(false); }}
                className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 ${active === n.key ? 'bg-[#E63935]/10 text-[#E63935]' : 'text-muted-foreground'}`}
                data-testid={`admin-nav-${n.key}`}>
                <n.icon size={16} weight={active === n.key ? 'fill' : 'regular'} /> {n.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Desktop left nav */}
      <aside className="hidden lg:block w-52 flex-shrink-0" data-testid="admin-sidenav">
        <div className="sticky top-4">
          <Card className="bg-card border-border">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-4 px-2">
                <div className="w-8 h-8 rounded-lg bg-[#E63935]/20 text-[#E63935] flex items-center justify-center"><Sparkle size={16} weight="fill" /></div>
                <div>
                  <p className="text-xs font-bold text-foreground leading-tight">CLARA AI</p>
                  <p className="text-[10px] text-muted-foreground leading-tight">AI ADOPTION DASHBOARD</p>
                </div>
              </div>
              <nav className="space-y-0.5">
                {NAV_ITEMS.map(n => (
                  <button key={n.key} onClick={() => setActive(n.key)}
                    className={`w-full text-left px-2.5 py-2 rounded-md text-sm flex items-center gap-2.5 transition-colors ${active === n.key ? 'bg-[#E63935]/10 text-[#E63935] font-semibold' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'}`}
                    data-testid={`admin-nav-${n.key}`}>
                    <n.icon size={16} weight={active === n.key ? 'fill' : 'regular'} /> {n.label}
                  </button>
                ))}
              </nav>
            </CardContent>
          </Card>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0" data-testid="admin-main">
        {active === 'adoption' && (
          <AdoptionOverview
            onOpenTeamAccess={() => setActive('team')}
            onOpenActivityFeed={() => setActive('activity')}
            onOpenFeatureUsage={() => setActive('features')}
            onOpenUserDetail={openDetail}
          />
        )}
        {active === 'team' && <TeamAccess />}
        {active === 'activity' && <ActivityFeed currentUser={currentUser} />}
        {active === 'features' && <FeatureUsage />}
        {active === 'reports' && <ComingSoon title="Reports" subtitle="Downloadable adoption, feature usage and coaching reports. Available in a future update." />}
        {active === 'settings' && <ComingSoon title="Clara Settings" subtitle="Configure adoption thresholds, notification cadence and admin preferences. Available in a future update." />}
      </main>

      <UserDetailDialog user={detailUser} open={!!detailUser} onOpenChange={(o) => { if (!o) setDetailUser(null); }} />
    </div>
  );
}
